import React, { useEffect, useMemo } from 'react';

import useInterval from '@use-it/interval';
import PropTypes from 'prop-types';

import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { omitKeys, noop } from '@adsk/alloy-react-helpers';

import use3LORelationships from './hooks/use3LORelationships';
import { useEntitiesCacheContext } from './providers/EntitiesCacheProvider';
import { useRelationshipsContext } from './providers/RelationshipsProvider';
import ReferencePickerView from './ReferencePickerView';

/**
 * A connected ReferencePicker component.
 *
 * This component allows you to use `ConnectedReferencePicker` somewhere under a `ReferencePickerProvider` component.
 * It's useful when you have other elements on screen that needs to share the same relationship data with this component.
 *
 * This component expects `ReferencePickerProvider` somewhere above it in the components tree.
 */
const ConnectedReferencePicker = React.forwardRef(
  (
    {
      onAddRelationships,
      onRemove,
      disableRelationshipService,
      allow3LOAdd,
      allow3LORemove,
      onAddSuccess = noop,
      onAddFailure = noop,
      onRemoveSuccess = noop,
      onRemoveFailure = noop,
      ...props
    },
    ref,
  ) => {
    const {
      sourceEntityId,
      sourceEntityDomain,
      sourceEntityType,
      pollingInterval,
      targetPlugins,
    } = props;
    const sourceIdAsArr = useMemo(
      () => (sourceEntityId ? [sourceEntityId] : []),
      [sourceEntityId],
    );
    const {
      relationshipsEntityMap,
      removeRelationshipById,
      relationshipDataBySource,
      fetchByIds,
      addRelationships,
    } = useRelationshipsContext();
    const { entitiesCache, addEntitiesToCache, updateEntitiesInCache } =
      useEntitiesCacheContext();
    const { is3LOSupported, add3LORelationships, remove3LORelationshipById } =
      use3LORelationships(targetPlugins);

    const {
      value: shouldHideReferencesThatAreNotAvailableInEntitlements,
      loading: loadingShouldHideReferencesThatAreNotAvailableInEntitlements,
    } = useAlloyFeatureFlag('hide-product-and-tools-reference-picker');

    useEffect(() => {
      fetchByIds(sourceIdAsArr, true);
    }, [sourceIdAsArr, sourceEntityDomain, sourceEntityType, fetchByIds]);

    useInterval(() => {
      fetchByIds(sourceIdAsArr, true);
    }, pollingInterval || null);

    const currentSourceRelationshipData = relationshipDataBySource?.[
      sourceEntityDomain
    ]?.[sourceEntityType]?.[sourceEntityId] || {
      loading: !disableRelationshipService,
      relationshipsData: [],
    };

    const relationshipData = useMemo(
      () => ({
        data: currentSourceRelationshipData.relationshipsData.map(
          ({ relationshipId, domain, type, entityId }) => ({
            domain,
            type,
            entityId,
            relationship: relationshipsEntityMap[relationshipId],
          }),
        ),
        loading: currentSourceRelationshipData.loading,
        error: currentSourceRelationshipData.error,
        retry: currentSourceRelationshipData.retry,
      }),
      // We don't want to calculate data every time `relationshipsEntityMap` is updated.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [currentSourceRelationshipData],
    );

    const onAddRelationshipsWrapper = (sourceEntity, entitiesToLink) => {
      const { domain, type } = entitiesToLink[0];

      if (allow3LOAdd && is3LOSupported({ domain, type })) {
        return add3LORelationships(sourceEntity, entitiesToLink, {
          includeFailedRelationships: true,
        }).then((relationships) => {
          if (relationships.fulfilled) {
            onAddSuccess(relationships.fulfilled);
          }

          if (relationships.rejected) {
            onAddFailure(relationships.rejected);
          }

          return relationships.fulfilled;
        });
      }

      return onAddRelationships(sourceEntity, entitiesToLink).then(
        (newRelationships) => {
          addRelationships(newRelationships);
          return newRelationships;
        },
      );
    };

    const onRemoveWrapper = (relationship) => {
      const { domain, type } = relationship.entities.find(
        (entity) =>
          !(
            entity.id === sourceEntityId &&
            entity.domain === sourceEntityDomain &&
            entity.type === sourceEntityType
          ),
      );

      if (allow3LORemove && is3LOSupported({ domain, type })) {
        return remove3LORelationshipById(relationship.id)
          .then((res) => {
            onRemoveSuccess(res, relationship);
            return res;
          })
          .catch((err) => {
            onRemoveFailure(err, relationship);
          });
      }

      return onRemove(relationship).then((res) => {
        removeRelationshipById(relationship.id);
        return res;
      });
    };

    return (
      <ReferencePickerView
        {...props}
        onAddRelationships={onAddRelationshipsWrapper}
        onRemove={onRemoveWrapper}
        ref={ref}
        loadingShouldHideReferencesThatAreNotAvailableInEntitlements={
          loadingShouldHideReferencesThatAreNotAvailableInEntitlements
        }
        relationshipsData={relationshipData}
        entitiesCache={entitiesCache}
        addEntitiesToCache={addEntitiesToCache}
        updateEntitiesInCache={updateEntitiesInCache}
        shouldHideReferencesThatAreNotAvailableInEntitlements={
          shouldHideReferencesThatAreNotAvailableInEntitlements
        }
      />
    );
  },
);

ConnectedReferencePicker.displayName = 'ConnectedReferencePicker';

ConnectedReferencePicker.propTypes = {
  ...omitKeys(ReferencePickerView.propTypes, [
    'relationshipsData',
    'entitiesCache',
    'addEntitiesToCache',
    'updateEntitiesInCache',
  ]),
  /**
   * Whether we want to disable fetching data from relationship service (when using `relationshipEntitiesList` for example)
   */
  disableRelationshipService: PropTypes.bool,
  /**
   * Optional number to poll new data from the server.
   * Default: undefined
   */
  pollingInterval: PropTypes.number,
  /**
   * Whether or not 3LO reference creation API can be used
   */
  allow3LOAdd: PropTypes.bool,
  /**
   * Whether or not 3LO reference deletion API can be used
   */
  allow3LORemove: PropTypes.bool,
  /**
   * Callback fired when references have been created with the 3LO reference creation API.
   * The general use case is for handling side effects in the consumer's app after 3LO reference creation.
   * Handler of shape: ({id, entities: { type, domain, id }[] }[]) => *
   */
  onAddSuccess: PropTypes.func,
  /**
   * Callback fired when a reference has been deleted with the 3LO reference deletion API.
   * The general use case is for handling side effects in the consumer's app after 3LO reference deletion.
   * Handler of shape: ({ deleted: [] }) => *
   */
  onRemoveSuccess: PropTypes.func,
};

export default ConnectedReferencePicker;
